<template>
<div class="relative">
    <div class="fixed white" style="width: 100%;" id="tool-bar">
    <v-row class="ma-0" style="width: 100%;" align="center" justify="start">
		<v-col cols="12" lg="auto" md="6" sm="12" xs="12">
			<slot name="breadcrumbs" :item="true"></slot>
		</v-col>
        <v-col cols="auto" sm="auto" xs="auto" md="6" lg="auto" class="d-flex flex-row justify-start">
            <bee-bond-up-down
                v-model="invoice_no"
                @keydown-enter-prevent="updateRouteId()"
                @programmatically-changed="updateRouteId()"
                :items="this.simplified_buy_invoices.map(c=>c.no)"
                class="medium-placeholder"
                :label="$t('InvoiceBuyPage.invoice-no')"
            ></bee-bond-up-down>
        </v-col>
        <v-col cols="12" sm="auto" xs="auto" md="auto" lg="auto" class="d-flex flex-column px-lg-0 justify-center">
			<bee-btn  
				@click="addInvoiceDialog = true"
				:content="$t('InvoiceBuyPage.new')"
				color="addingColor"
				context="invoice"
                icon="fa-plus"
				:action-name="actions.CREATE"
                :disabled="file_is_locked"
			></bee-btn>
		</v-col>
        <v-col cols="12" sm="auto" xs="auto" md="auto" lg="auto" class="d-flex flex-column justify-center">
			<bee-btn  
				@click="dialogDeleteInvoice = true"
				:content="$t('InvoiceBuyPage.delete')"
				color="deletingColor"
				context="invoice"
                icon="fa-trash"
				:action-name="actions.DELETE"
                :disabled="file_is_locked"
			></bee-btn>
		</v-col>
    </v-row>
    <v-layout class="mx-3 mb-3 my-border rounded-lg d-flex flex-row wrap" align="center">
        <v-col cols="auto" lg="3" md="auto" sm="auto" xs="auto" class="pb-0 pe-lg-0 d-flex flex-column">
            <bee-account-autocomplete
            :disabled="buy_invoice.is_posted || file_is_locked"
            context="invoice"
            :action-name="actions.EDIT"
            :hide-details="false" 
            :rules="[rules.required]"
            :label="$t('InvoiceBuyPage.account')"
            color="primary"
            v-model="buy_invoice.external_account_id"
            :items="configs.accounts.filter(c=>c.type == 2)"
            @change="update()"
            :with-balance="true"
            ></bee-account-autocomplete>
        </v-col>
        <v-col cols="auto" lg="auto" md="auto" sm="auto" xs="auto" class="pb-0 pb-lg-0 d-flex flex-column">
        <bee-date-picker
            :disabled="buy_invoice.is_posted || file_is_locked"
            context="invoice"
            :action-name="actions.EDIT"
            @blur="update()"
            :label="$t('InvoiceBuyPage.invoice-date')"
            v-model="buy_invoice.date"
            :rules="[rules.date]"
            :hide-details="false"
        ></bee-date-picker>
        </v-col>
        <v-col cols="12" sm="auto" xs="auto" md="auto" lg="2" class="d-flex flex-column pb-lg-0 pb-0 ps-lg-0">
            <bee-autocomplete
            :disabled="buy_invoice.is_posted || file_is_locked" 
            context="invoice"
            :action-name="actions.GET"
            item-text="name"
            item-value="id"
            :label="$t('InvoiceBuyPage.warehouse')"
            :items="(userTypePermission === userType.warehouseKeeper && multipleWarehouses) ? warehousesForKeeper : warehouses"
            v-model="default_warehouse_id"
            :clearable="false"
            :hide-details="false"
            @change="changeWarehouse()"
            ></bee-autocomplete>
        </v-col>
        <v-col cols="auto" lg="auto" md="auto" sm="auto" xs="auto" class="pb-0 pb-lg-0 px-md-0 d-flex justify-center flex-column">
            <bee-text-field :disabled="buy_invoice.is_posted || file_is_locked" context="invoice" :action-name="actions.EDIT" @change="update()" style="width: 100px !important" :label="$t('InvoiceBuyPage.document')" :hide-details="false" :rules="[rules.required]" v-model="buy_invoice.doc_no"></bee-text-field>
        </v-col>
        <v-spacer class="hidden-sm-and-down"></v-spacer>
        <v-col v-if="!isWarehousePermission" cols="auto" lg="auto" md="auto" sm="auto" xs="auto" class="pb-lg- pt-lg-1 d-flex flex-column">
            <bee-outlined-header-info :head="$t('InvoiceBuyPage.net-invoice')" style="line-height: 1.5rem;" border-color="transparent" class="pa-lg-0" :column="on_lgAndUp">
                <template  v-slot:content="{}">
                    <bee-money-label :number="total_net" type="debit" :withColor="true" empty-state="_"></bee-money-label>
                </template>
            </bee-outlined-header-info>
        </v-col>
        <v-col v-if="!isWarehousePermission" cols="auto" sm="auto" xs="auto" md="auto" lg="auto" class="d-flex flex-column py-lg-0 ps-lg-0">
            <bee-btn context="invoice" :action-name="actions.EDIT" 
                :icon="buy_invoice.is_posted ? 'fa-envelope-open' : 'fa-envelope'"
                :outer-class="on_lgAndUp ? 'fill-height text-wrap' : ''" 
                :content="buy_invoice.is_posted ? $t('InvoiceBuyPage.edit') : $t('InvoiceBuyPage.save')"
                :disabled="file_is_locked"
                :loading="updateInvoiceLoading" 
                :color="buy_invoice.is_posted ? 'deletingColor' : 'blueGray'" 
                @click="buy_invoice.is_posted ? unPosting() : posting()">
            </bee-btn>
        </v-col>

        <!-- invoice posting progress circular -->
        <v-col cols="auto" class="d-flex justify-lg-end py-1">
            <router-link style="text-decoration: none;" :to="{name: 'invoices', query: { type: 1 }}">
                <bee-progress-circular
                    :size="60"
                    :width="6"
                    :title="bond_posting_ratio_caption"
                    :value="bond_posting_ratio"
                    :tooltip="bond_posting_ratio_tooltip"
                ></bee-progress-circular>
            </router-link>
        </v-col>
        
        <v-col cols="12" sm="12" xs="12" md="12" lg="1" 
            class="d-flex flex-column align-center pa-lg-0"
            :class="[
                buy_invoice.is_posted ? 'postedColor' : 'unpostedColor',
                on_lgAndUp ? 'border-round-end' : 'border-round-bottom'
            ]"
        >
            <span class="d-flex justify-center text-center align-center fill-height text-wrap white--text font-weight-bold">
                {{buy_invoice.is_posted ? '' : $t('InvoiceBuyPage.not')}}
                {{$t('InvoiceBuyPage.posted')}}
            </span>
        </v-col>
    </v-layout>
    </div>

    <div class="ma-auto absolute" id="main" style="width: 100%; height: auto">
    <div class="px-3">
        <bee-smart-table
            v-if="false"
            v-model="tableInputs"
            mode="materials" 
            context="invoice" 
            :action-name="actions.CREATE" 
            ref="table" 
            :with-color="false" 
            :zebra="true" 
            empty-state="dashes" 
            :hide-footer="true"
            @cell-change="entries[$event.index][$event.header.value] = $event.value"
            :addable="addable && !file_is_locked" :editable="!buy_invoice.is_posted && !file_is_locked" :configs="[]" @reset-data="fetchInvoiceEntry($event)" 
            :headers="headers"
            :items="entries || []"
            :show-menu-actions="true"
            @row-edited="updateEntry($event)"
            @row-added="addInvoiceEntry($event)" 
            @delete-item="deleteInvoiceEntry($event)"
            :right-click-menu-items="actionsList"
            :messagesSelectRowValidation="messagesSelectRowValidation"
            @nblur="handlBlurBoundTable"
            >
        </bee-smart-table>
        
        <bee-handy-smart-table
            v-model="tableInputs"
            :headers="tableHeaders"
            :items="entries || []"
            :loading="loading || entryLoading"
            ref="table"
            zebra
            dense
            fixed-header
            :height="$vuetify.breakpoint.height - 338"
            :items-per-page="30"
            hot-inputs="lazy"
            :disable-input-fields="loading || entryLoading || updateInvoiceLoading"
            pagination-on-scroll
            hide-shown-rows-text
            hide-inputs-details
            hide-no-data-area
            with-columns-divider
            stay-in-input-fields
            :show-add-row="!buy_invoice.is_posted && Boolean($route.params.id) && !isViewer"
            :editable-rows="!buy_invoice.is_posted && Boolean($route.params.id) && !isViewer"
            :data-table-props="{ disableSort: true }"
            :validation-messages.sync="validationMessages"
            :context-menu-items="contextmenuItems"
            :disable-context-menu="buy_invoice.is_posted || loading || entryLoading || updateInvoiceLoading || isViewer"
            :scroll-to-row-expression="(item) => item.id === entryId || item.id === entries[entries.length - 1].id"
            @context-menu-edit="({ item }) => entryId = item.id"
            @context-menu-delete="({ item }) => deleteInvoiceEntry(item)"
            @edit-cancelled="entryId = null"
            @keydown.enter="entryHandler"
            @reached-last-input="entryHandler"
        >
            <template v-slot:input.warehouseId="{ attr, on }">
                <v-autocomplete
                    v-on="on"
                    v-bind="attr"
                    :items="
                        userTypePermission === userType.warehouseKeeper && multipleWarehouses
                            ? warehousesForKeeper
                            : warehouses
                    "
                    :item-value="
                        userTypePermission === userType.warehouseKeeper && multipleWarehouses
                            ? 'warehouse_id'
                            : 'id'
                    "
                    item-text="name"
                />
            </template>
            <template v-slot:input.material_delivery_status="{ attr, on }">
                <v-autocomplete 
                    :items="materialDeliveryStatus"
                    item-text="name"
                    item-value="id"
                    hide-details
                    dense
                    v-on="on"
                    v-bind="attr"
                />
            </template>
        
            <template v-slot:input.materialId="{ attr, on }">
                <bee-material-autocomplete
                    v-on="on"
                    v-bind="attr"
                    :inside-table="true"
                    :with-balance="true"
                    :items="
                        warehouse_materials.length
                            ? warehouse_materials
                            : userTypePermission === userType.warehouseKeeper && multipleWarehouses
                                ? materials.filter(c => c.warehouse_id === tableInputs.warehouse && c.type === 2)
                                : materials.filter(c => c.type === 2)
                    "
                    style="padding-bottom: 2px !important;"
                />
            </template>
            <template v-slot:no-input.hint>
                <v-menu bottom v-if="validationMessages.length > 0">
                    <template v-slot:activator="{ on, attr }">
                        <v-icon
                            v-on="on"
                            v-bind="attr"
                            color="danger-color"
                            size="18"
                        >mdi-information</v-icon>
                    </template>

                    <v-list>
                        <v-list-item
                            v-for="(message, i) in inputsValidationMessages"
                            :key="i"
                            dense
                            class="px-2"
                        >
                            <v-list-item-icon class="me-0 mb-0">
                                <v-icon color="danger-color" x-small>mdi-circle-half</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="danger-color--text">
                                {{message}}
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
        </bee-handy-smart-table>

        <!-- alertMessageDialog -->
        <v-dialog v-model="alertMessageDialog" width="400">
            <v-card>
                <v-card-title>
                    {{ $t('InvoiceBuyPage.alert-message') }}
                </v-card-title>

                <v-card-text>
                    {{ $t('InvoiceBuyPage.confirm-message') }}
                </v-card-text>
                <v-card-actions class="pt-0">
                    <v-spacer />
                    <v-btn @click="alertMessageDialog = false; !isEdit ? addRowForEntriesDialog(InvoicesTransactionType.payment) : editRowForEntriesDialog(InvoicesTransactionType.payment)" color="greenDarken1" text>
                        {{ $t('buttons.ok') }}
                    </v-btn>

                    <v-btn 
                        color="redDarken1" 
                        @click="
                            alertMessageDialog =  false;
                            $refs.tableReceipt.resetInputs({
                                date: moment().format('YYYY-MM-DD')
                            })
                        " 
                        text
                    >
                        {{ $t('buttons.cancel') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <!-- dialog payment -->
        <v-dialog v-model="dialogPaymentOrExpenses" width="700" >
            <v-card>
                <v-card-title>
                    {{ !isExpenses ? $t('InvoiceBuyPage.payments') : $t('InvoiceBuyPage.expenses') }}
                </v-card-title>

                <v-card-text>
                    <bee-handy-smart-table
                        ref="tablePayment"
                        v-model="inputsPaymentsOrExpenses"
                        :items="entriesDialog"
                        :headers="headersPaymentsOrExpenses"
                        :items-per-page="-1"
                        :data-table-props="{ disableSort: true }"
                        dense
                        zebra
                        hide-default-footer
                        :disable-input-fields="submitLoading"
                        show-add-row
                        hide-inputs-details
                        :editable-rows="!isViewer"
                        :loading="submitLoading"
                        @keypress.enter="confirmTotalPaymentsForTotalNet()"
                    >
                        <template v-slot:input.date="{ on, attr }">
                            <bee-date-input 
                                label=" "
                                fit-inputs
                                dense
                                input-format-order="DD-MM-YYYY"
                                v-on="on"
                                v-bind="attr"
                            />
                        </template>

                        <template v-slot:input.account_id="{ on, attr }">
                            <bee-account-autocomplete
                                context="invoice"
                                :action-name="actions.CREATE"
                                :hide-details="false"             
                                color="primary" 
                                :items="configs.accounts.filter(c=>c.type == 2)"
                                :with-balance="true"
                                v-on="on"
                                v-bind="attr"
                            ></bee-account-autocomplete>
                        </template>

                        <!-- no-input action -->
                        <template v-slot:no-input.actions>
                            <div class="d-flex justify-center">
                                <tooltip :text="$t('buttons.add')" top>
                                    <v-btn
                                        icon
                                        small
                                        color="greenDarken1"
                                        @click="
                                            confirmTotalPaymentsForTotalNet()
                                        "
                                        :disabled="submitLoading"
                                    >
                                        <v-icon>
                                            mdi-check-circle-outline
                                        </v-icon>
                                    </v-btn>
                                </tooltip>
                            </div>
                        </template>

                        <template v-if="!buy_invoice.is_posted && !isViewer" v-slot:item.actions="{ index, item }">
                            <div class="d-flex justify-center">
                                <v-btn
                                    icon
                                    small
                                    color="warning-color"
                                    @click="() => {
                                        transactionId = item.id
                                        $refs.tablePayment.activeEditForRow(index);
                                        editRowIndex = index;
                                        isEdit = true;
                                        $nextTick(() => {
                                            $refs.tablePayment.resetInputsFocus()
                                        })
                                    }"
                                >
                                    <v-icon>
                                        mdi-pencil
                                    </v-icon>
                                </v-btn> 

                                <v-btn
                                    icon
                                    small
                                    color="deletingColor"
                                    @click="transactionId = item.id; deleteRowForPayments()"
                                >
                                    <v-icon>
                                        mdi-delete
                                    </v-icon>
                                </v-btn>  
                            </div>
                            
                        </template>
                    </bee-handy-smart-table>
                </v-card-text>

                <v-card-actions class="pt-0">
                    <v-spacer />
                    <v-btn @click="dialogPaymentOrExpenses = false" color="greenDarken1" text>
                        {{ $t('buttons.ok') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>


        <v-form v-model="valid" ref="form">
        <v-dialog transition="dialog-top-transition" v-model="addInvoiceDialog" max-width="500">
            <v-card>
            <v-card-title class="headline">{{$t('InvoiceBuyPage.add-invoice')}}</v-card-title>
            <v-card-text>
            <v-row class="ma-0">
                <v-col cols="auto" lg="12" class="pa-0">
                    <bee-date-picker context="invoice" :action-name="actions.CREATE" :label="$t('InvoiceBuyPage.invoice-date')" v-model="invoice_template.date" :rules="[rules.date]"></bee-date-picker>
                </v-col>
                <v-col cols="auto" lg="9" class="py-0 d-flex flex-column">
                    <bee-account-autocomplete
                    context="invoice"
                    :action-name="actions.CREATE"
                    :hide-details="false" 
                    :rules="[rules.required]"
                    :label="$t('InvoiceBuyPage.account')"
                    color="primary"
                    v-model="invoice_template.external_account_id"
                    :items="configs.accounts.filter(c=>c.type == 2)"
                    :with-balance="true"
                    ></bee-account-autocomplete>
                </v-col>
                <v-col cols="auto" lg="auto" class="py-0 ps-0">
                    <bee-text-field context="invoice" :action-name="actions.EDIT" style="width: 100px !important" :label="$t('InvoiceBuyPage.document')" :hide-details="false" :rules="[rules.required]" v-model="invoice_template.doc_no"></bee-text-field>
                </v-col>
                <v-col cols="auto" lg="12" class="py-0">
                    <bee-text-field context="invoice" :action-name="actions.EDIT" :label="$t('InvoiceBuyPage.notes')" v-model="invoice_template.notes" :hide-details="true"></bee-text-field>
                </v-col>
            </v-row>
            </v-card-text>
            <v-card-actions class="ms-3">
                <v-btn color="greenDarken1" text @click="create()" :loading="loading" :disabled="!valid || file_is_locked">{{$t('InvoiceBuyPage.create')}}</v-btn>
                <v-btn color="redDarken1" text @click="reSet()" >{{$t('InvoiceBuyPage.cancel')}}</v-btn>
                <v-spacer></v-spacer>
            </v-card-actions>
            </v-card>
        </v-dialog>
        </v-form>
        <bee-dialog
            @handler="confirmDeleteInvoiceEntry()"
            @close="dialogDeleteEntry = false"
            v-model="dialogDeleteEntry"
            :title="$t('InvoiceBuyPage.delete-entry')"
            :message="$t('InvoiceBuyPage.are-you-sure-you-want-to-delete-the-entry')"
        ></bee-dialog>
        <bee-dialog
            @handler="deleteInvoice()"
            @close="dialogDeleteInvoice = false"
            v-model="dialogDeleteInvoice"
            :title="$t('InvoiceBuyPage.delete-invoice')"
            :message="$t('InvoiceBuyPage.are-you-sure-you-want-to-delete-the-invoice-that-will-delete-the-associated-bond')"
        ></bee-dialog>
    </div>
    <v-bottom-navigation id="footer" height="auto" app horizontal class="px-3">
    <v-row class="fill-height ma-0" style="width: 100%" align="center">            
        <v-col
            v-if="useBuyInvoicePayments"
            cols="auto"
            lg="auto"
            md="auto"
            sm="auto"
            xs="auto"
            class="align-center justify-start d-flex pe-lg-0"
        >
            <!-- payments sum --> 
            {{$t('InvoiceBuyPage.total-payments') + ': ' }}
            <span 
                class="ms-2 me-4 font-weight-bold"
                :class="{
                    'green--text': total_net === totalPayments,
                    'red--text': total_net !== totalPayments
                }"
            > 
                {{ (totalPayments ? (totalPayments).toLocaleString() : 0 ) }} 
            </span>
            <v-btn
                :color="(!buy_invoice.is_posted && !isViewer ? 'success' : 'info-color') + ' white--text'"
                class="px-0 rounded-xl" 
                min-width="0"
                width="28"
                height="28"
                @click="dialogPaymentOrExpenses = true" 
                :disabled="loading && !buy_invoice.is_posted && Boolean($route.params.id)"
            >
                <v-icon color="white" size="22" class="mx-0"> mdi-{{!buy_invoice.is_posted && !isViewer ? 'plus' : 'eye'}} </v-icon>  
            </v-btn>    
        </v-col>

        <!-- Expenses -->
        <v-col
            v-if="useBuyInvoiceExpenses"
            cols="auto"
            lg="auto"
            md="auto"
            sm="auto"
            xs="auto"
            class="align-center justify-start d-flex pe-lg-0"
        >
            <!-- payments sum --> 
            {{$t('InvoiceBuyPage.total-expenses') + ': ' }}
            <span 
                class="ms-2 me-4 font-weight-bold"
            > 
                {{ (totalExpenses ? (totalExpenses).toLocaleString() : 0 ) }} 
            </span>
            <v-btn
                :color="(!buy_invoice.is_posted && !isViewer ? 'success' : 'info-color') + ' white--text'"
                class="px-0 rounded-xl" 
                min-width="0"
                width="28"
                height="28"
                @click="isExpenses = true; dialogPaymentOrExpenses = true" 
                :disabled="loading"
            >
                <v-icon color="white" size="22" class="mx-0"> mdi-{{!buy_invoice.is_posted && !isViewer ? 'plus' : 'eye'}} </v-icon>  
            </v-btn>    
        </v-col>

        <!-- <v-spacer/> -->
        <v-col cols="auto" lg="4" md="auto" sm="auto" xs="auto" class="align-center justify-start d-flex pe-lg-0">
            <bee-text-field :disabled="buy_invoice.is_posted || file_is_locked" context="invoice" :action-name="actions.EDIT" @change="update()" :label="$t('InvoiceBuyPage.notes')" v-model="buy_invoice.notes" :hide-details="true"></bee-text-field>
        </v-col>
        <v-spacer/>
        <v-col
            v-if="!isWarehousePermission"
            cols="auto"
            lg="auto"
            md="auto"
            sm="auto"
            xs="auto"
            class="align-center justify-start d-flex"
        >
            <bee-money-field context="invoice" :disabled="buy_invoice.is_posted || file_is_locked || !total" :action-name="actions.EDIT" @blur="update()" hide-details="true" :label="$t('InvoiceBuyPage.discount')" v-model="buy_invoice.discount_total" :rules="[rules.money]"></bee-money-field>
        </v-col>

        <v-col v-if="!isWarehousePermission" cols="auto" lg="auto" md="auto" sm="auto" xs="auto" class="align-start flex-column d-flex">
        <bee-outlined-header-info :head="$t('InvoiceBuyPage.total')" border-color="transparent" class="pa-lg-0">
            <template  v-slot:content="{}">
                <bee-money-label :number="total" type="debit" :withColor="true" empty-state="_"></bee-money-label>
            </template>
        </bee-outlined-header-info>

        <bee-outlined-header-info v-if="!isWarehousePermission" :head="$t('InvoiceBuyPage.net-invoice')" border-color="transparent" class="pa-lg-0">
            <template  v-slot:content="{}">
                <bee-money-label :number="total_net" type="debit" :withColor="true" empty-state="_"></bee-money-label>
            </template>
        </bee-outlined-header-info>
        </v-col>
    </v-row>
    </v-bottom-navigation>
    </div>
</div>
</template>
<script>
import rules from "../../../validation";
import actions from "../../../permissions/actions-names.js"
import { mapState, mapGetters } from "vuex";
import  store  from "../../../store/index.js";
import { invoiceEntryDetectedValidation } from "./helper/validation-detected-invoice-entry";
import { refactorRecord } from "./helper/operations-calculated-properties.js"
import EventBus from '../../../event-bus'
import { userType, invoiceType, bondType, InvoicesTransactionType, materialDeliveryStatus } from '@/helpers/enums';
import { multipleWarehouses, isMangeUnitAndRegionAndPatchAndLot, separationOfInvoices, useBuyInvoicePayments,useBuyInvoiceExpenses, useMaterialStatusDeliver } from '@/configs/global'
import moment from 'moment'

export default {
data: () => 
    ({
        dialogDeleteInvoice: false,
        loading: false,
        valid: false,
        addInvoiceDialog: false,
        default_warehouse_id: null,
        selected_warehouse_id: null,
        invoice_no: null,
        ...rules,
        messagesSelectRowValidation: [],
        actions,
        invoice_template:{
            external_account_id: null,
            date: new Date().toISOString().substr(0, 10).toString(),
            doc_no: null,
            notes: null,
        },
        linkedInvoiceRules: {
            price: true, quantity: true, count: true, material_id: true
        },
        entry: null,
        dialogDeleteEntry: false,

        updateInvoiceLoading: false,
        userType,
        invoiceType,
        multipleWarehouses,
        isMangeUnitAndRegionAndPatchAndLot,
        separationOfInvoices,
        useBuyInvoicePayments,
        dialogPaymentOrExpenses: false,
        inputsPaymentsOrExpenses: {
            date: moment().format('YYYY-MM-DD')
        },
        // payments: [],
        editRowIndex: null,
        isEdit: false,

        // inputs
        entryLoading: false,
        entryId: null,
        validationMessages: [],
        tableInputs: {},
        InvoicesTransactionType,
        submitLoading: false,
        transactionId: null,
        totalPayments: null,
        alertMessageDialog: false,

        useBuyInvoiceExpenses,
        totalExpenses: null,
        isExpenses: false,
        entriesDialog: [],
        materialDeliveryStatus,
        useMaterialStatusDeliver
    }),

    watch: {
        '$route.params.id' (newVal) {
            // this.$refs.table.openLoading();
            this.loading = true;
            this.$store.dispatch("invoices/fetch", {
                invoiceId: Number(newVal),
                invoiceType: invoiceType.buy
            }).then((data) => {
                this.invoice_no = data.no;
                this.changeWarehouse();
                this.loading = false;
     
                this.totalPayments = data.transactions.length ? data.transactions.filter(c => c.type === InvoicesTransactionType.payment).map(c => c.amount?.amount).reduce((a, b) => a + b) : null

                this.totalExpenses = data.transactions.length ? data.transactions.filter(c => c.type === InvoicesTransactionType.expensesBuy).map(c => c.amount?.amount).reduce((a, b) => a + b) : null
            })
        },
        dialogPaymentOrExpenses (val) {
            if(val) {
                this.getAllPaymentsOrExpenses(this.isExpenses ? InvoicesTransactionType.expensesBuy : InvoicesTransactionType.payment)
                this.inputsPaymentsOrExpenses.date = moment().format('YYYY-MM-DD') 
            } else {
                this.entriesDialog = []
                this.transactionId = null
                this.isExpenses = false
                this.$refs.tablePayment.resetInputs({
                    date: moment().format('YYYY-MM-DD')
                })
            }
        }
    },

computed: {
    ...mapState("configs" , ["configs"]),
    ...mapState("invoices" , ["buy_invoice"]),
    ...mapState("invoices" , ["simplified_buy_invoices"]),
    ...mapState("warehouses" , ["warehouses"]),
    ...mapState("materials" , ["materials"]),
    ...mapState({
        userTypePermission: state => state.permissions.user_type,
        currentFinancialFile: state => state.financialFiles.current_financial_file,
        numOfNotPostedInvoices: state => state.configs.configs.num_of_not_posted_invoices.filter(c => c.invoice_type === invoiceType.buy),
        numOfTotalBuyInvoices: state => state.configs.configs.num_of_total_invoices.filter(c => c.invoice_type === invoiceType.buy),
        warehousesForKeeper: state => state.warehouses.warehousesForKeeper
        
    }),
    ...mapGetters({
        getWarehouseById: 'warehouses/getById',
        getMaterialById: 'materials/getMaterialById',
        isViewer: 'auth/isViewer'
    }),
    warehouse_materials(){
        return this.materials.filter(c=> this.selected_warehouse_id ? c.warehouse_id == this.selected_warehouse_id : true)
                    .filter(c=>c.type == 2)
    },
    file_is_locked(){ return this.configs?.financial_file?.is_locked },
    on_mdAndUp(){ return (this.$vuetify.breakpoint.mdAndUp) },
    addable(){
		return this.buy_invoice.is_posted || !this.invoice_no ? false : true
	},
    total(){ 
        return this.buy_invoice?.invoice_entries?.map(c=> c.calculated_total_amount?.amount ? c.calculated_total_amount.amount : 0 ).reduce((previousValue, currentValue) => previousValue + currentValue, 0) },
    on_lgAndUp(){ return(this.$vuetify.breakpoint.lgAndUp) },
    
    tableHeaders(){
        const headers = [
            {
                text: this.$t('InvoiceBuyPage.headers.no'),
                name: 'no',
                value: 'no',
                width: '3%',
                noInput: true,
            },
            {
                text: this.$t('InvoiceBuyPage.headers.warehouse'),
                name: 'warehouseId',
                value: 'warehouse_id',
                width: !this.isWarehousePermission ? '17%' : '19.5%',
                setValueExpr: val => {
                    const warehouse = this.getWarehouseById(val);
                    return warehouse ? warehouse.name : null;
                }
            },
            {
                text: this.$t('InvoiceBuyPage.headers.material'),
                name: 'materialId',
                value: 'material_id',
                width: '28%',
                editable: true,
                inputRules: [rules.rules.required],
                setValueExpr: val => {
                    const material = this.getMaterialById(val);
                    return material ? material.name : null;
                }
            },
            {
                text: this.$t('InvoiceBuyPage.headers.quantity'),
                name: 'quantity',
                value: 'quantity',
                type: 'number-format',
                width: '6%',
                inputRules: [rules.rules.required],
            },
            {
                text: this.$t('InvoiceBuyPage.headers.count'),
                name: 'count',
                value: 'count',
                type: 'number-format',
                width: '6%',
            },
            {
                text: this.$t('InvoiceBuyPage.headers.notes'),
                name: 'notes',
                value: 'notes',
            },
            {
                text: '',
                name: 'hint',
                value: 'hint',
                noInput: true,
                noNotAvailableText: true,
                divider: false,
                width: !this.isWarehousePermission ? '2%' : '1%',
            },
        ]
        if(useMaterialStatusDeliver) {
            headers.splice(5, 0, {
                text: this.$t('InvoiceBuyPage.headers.delivery-status'),
                name: 'material_delivery_status',
                value: 'material_delivery_status',
                noNotAvailableText: true,
                setValueExpr: val => this.materialDeliveryStatus.find(c => c.id === val)?.name
            })
        }
        if(isMangeUnitAndRegionAndPatchAndLot) {
            headers.splice(5,0, 
            {
                text: 'رقم الباتش',
                name: 'patchNumber',
                value: 'patchNumber',
                editable: true,
                width: '10%'
            },
            {
                text: 'رقم اللوت',
                name: 'lotNumber',
                value: 'lotNumber',
                editable: true,
                width: '10%'
            },
            {
                text: 'تاريخ الانتهاء',
                name: 'endDate',
                value: 'endDate',
                type: 'date',
                editable: true,
                width: '13%'
            })
        }
        if(!this.isWarehousePermission) { 
            headers.splice(4, 0, {
                text: this.$t('InvoiceBuyPage.headers.price'),
                name: 'price',
                value: 'price',
                type: 'number-format',
                width: '6%',
                inputRules: [rules.rules.required],
            },
            {
                text: this.$t('InvoiceBuyPage.headers.total-amount'),
                name: 'calculatedTotalAmount',
                value: 'calculated_total_amount',
                type: 'number-format',
                width: '10%',
                setInputExpr: (inputs, activeInput) => {
                    if (activeInput === 'calculatedTotalAmount') {
                        if (inputs.quantity) {
                            this.tableInputs.price = inputs.calculatedTotalAmount / inputs.quantity;
                            this.$nextTick(() => {
                                this.tableInputs.calculatedTotalAmount = inputs.quantity * inputs.price;
                            })
                        } else {
                            return inputs.calculatedTotalAmount;
                        }
                    } else if (['quantity', 'price'].includes(activeInput)) {
                        return inputs.quantity * inputs.price;
                    } else {
                        return inputs.calculatedTotalAmount
                    }
                }
            })
        }
        return headers
    },
    headers(){
        const headers = [
            {
                text: this.$t('InvoiceBuyPage.headers.no'),
                value: 'no',
                type: 'string', 
                width: !this.isWarehousePermission ? '3%' : '19.5%',
                editable: false,
                settings: { context: 'invoice', actionName: actions.GET }
            },
            {
                text: this.$t('InvoiceBuyPage.headers.warehouse'),
                value: 'warehouse_id',
                type: 'autocomplete',
                width: !this.isWarehousePermission ? '17%' : '19.5%',
                editable: true,
                required: true,
                settings: { itemText: 'name', items: this.warehouses , clearable: true,
                itemValue: this.userTypePermission === userType.warehouseKeeper && multipleWarehouses ? 'warehouse_id' : 'id', contex: 'warehouses', actionName: actions.GET }
            },
            {
                text: this.$t('InvoiceBuyPage.headers.material'),
                value: 'material_id',
                type: 'materials-autocomplete',
                width: '20%',
                editable: true, rules:[this.linkedInvoiceRules.material_id],
                settings: { itemText: undefined, items: this.materials.filter(c => c.type === 2), subItems: this.warehouse_materials }
            },
            {
                text: this.$t('InvoiceBuyPage.headers.quantity'),
                value: 'quantity',
                type: 'numeric',
                width: !this.isWarehousePermission ? '15%' : '19.5%',
                editable: true, rules:[this.linkedInvoiceRules.quantity],
                settings: { context: 'invoice', actionName: actions.GET }
            },
            {
                text: this.$t('InvoiceBuyPage.headers.count'),
                value: 'count',
                type: 'numeric',
                width: !this.isWarehousePermission ? '15%' : '19.5%',
                editable: true,
                settings: { context: 'invoice', actionName: actions.GET }
            },
            {
                text: this.$t('InvoiceBuyPage.headers.notes'),
                value: 'notes',
                type: 'string',
                width: !this.isWarehousePermission ? '15%' : '19.5%',
                editable: true,
                required: true,
                settings: { context: 'invoice', actionName: actions.GET } 
            },
            {
                text: '',
                value: 'hint',
                type: 'hint',
                width: !this.isWarehousePermission ? '2%' : '1%',
                editable: false
            },
        ]
        if(isMangeUnitAndRegionAndPatchAndLot) {
            headers.splice(5,0, 
            {
                text: 'رقم الباتش',
                name: 'patchNumber',
                value: 'patchNumber',
                type: 'string',
                editable: true,
                settings: { context: 'invoice', actionName: actions.GET },
                width: '10%'
            },
            {
                text: 'رقم اللوت',
                name: 'lotNumber',
                value: 'lotNumber',
                type: 'string',
                editable: true,
                settings: { context: 'invoice', actionName: actions.GET },
                width: '10%'
            },
            {
                text: 'تاريخ الانتهاء',
                name: 'endDate',
                value: 'endDate',
                type: 'date',
                editable: true,
                settings: { context: 'invoice', actionName: actions.GET },
                width: '13%'
            })
        }
        if(!this.isWarehousePermission) { 
            headers.splice(4, 0, {
                text: this.$t('InvoiceBuyPage.headers.price'),
                value: 'price',
                type: 'numeric',
                width: '15%',
                editable: true, rules:[this.rules.money && this.linkedInvoiceRules.price],
                settings: { context: 'invoice', actionName: actions.GET }
            },
            {
                text: this.$t('InvoiceBuyPage.headers.total-amount'),
                value: 'calculated_total_amount',
                type: 'numeric',
                width: '15%',
                editable: true,
                settings: { context: 'invoice', actionName: actions.GET }
            },)
            // headers.splice(7,0 , 
            // // {
            //     //     text: this.$t('InvoiceBuyPage.headers.price'),
            //     //     value: 'price',
            //     //     type: 'numeric',  
            //     //     width: '15%',
            //     //     editable: true, rules:[this.rules.money && this.linkedInvoiceRules.price],
            //     //     settings: { context: 'invoice', actionName: actions.GET }
            //     // },
            //     // {
            //     //     text: this.$t('InvoiceBuyPage.headers.total-amount'),
            //     //     value: 'calculated_total_amount',
            //     //     type: 'numeric',
            //     //     width: '15%',
            //     //     editable: true,
            //     //     settings: { context: 'invoice', actionName: actions.GET }
            //     // },
            //     )

        }
        return headers
    },
    
    // youHavePermission() {
    //     return this.userTypePermission !== userType.warehouseKeeper && this.userTypePermission !== userType.warehouseManger
    // },
    isWarehousePermission() {
        return (this.userTypePermission === userType.warehouseKeeper && separationOfInvoices) || (this.userTypePermission === userType.warehouseManger && separationOfInvoices)
    },

    entries(){
        return this.buy_invoice.invoice_entries?.map(c=> ({ no: c.no, 
            warehouse_id: this.materials.find(x=>x.id == c.material_id).warehouse_id, 
            id: c.id,
            material_id: c.material_id,
            quantity: c.quantity.quantity,
            count: c.count,
            price: c.price.amount,
            calculated_total_amount: c.calculated_total_amount.amount,
            notes: c.notes,
            type: c.type,
            material_delivery_status: c.material_delivery_status
        }))
    },
    total_net() { 
        return this.buy_invoice.discount_total?.amount ? 
            this.total - this.buy_invoice.discount_total.amount : this.total 
    },
    actionsList(){
        return [
            {
                name: 'edit',
                disabled: this.buy_invoice.is_posted,
                action_name: actions.EDIT,
                text: this.$t('InvoiceBuyPage.context-menu.edit')
            }, 
            {
                name: 'delete-item',
                disabled: this.buy_invoice.is_posted,
                action_name: actions.DELETE,
                text: this.$t('InvoiceBuyPage.context-menu.delete')
            }
        ]
    },

    bond_posting_ratio() {
        return this.numOfTotalBuyInvoices.length
            ? 100 -
            ((this.numOfNotPostedInvoices.length ? this.numOfNotPostedInvoices[0].count : 0) * 100) /
                this.numOfTotalBuyInvoices[0].count
        : 100   
    },

    bond_posting_ratio_tooltip() {
        return this.numOfNotPostedInvoices.length
            ? `${this.numOfNotPostedInvoices[0].count} ${this.$t('unposted-invoices')}`
            : this.$t('unposted-invoices');
    },

    bond_posting_ratio_caption() {
        return `${this.numOfNotPostedInvoices.length ? this.numOfNotPostedInvoices[0].count : 0}/${this.numOfTotalBuyInvoices.length ? this.numOfTotalBuyInvoices[0].count : 0}`;
    },
    headersPaymentsOrExpenses() {
        const headers = [
            {
                text: this.$t('headers.date'),
                value: 'date',
                name: 'date',
                width: '20%',
                inputRules: [this.rules.required]
            },
            {
                text: this.$t('headers.amount'),
                name: 'amount',
                value: 'amount.amount',
                type: 'number',
                inputRules: [this.rules.required]
            },
            {
                text: this.$t('headers.note'),
                name: 'notes',
                value: 'notes',
                width: '40%'
            },
            {
                text: '',
                name: 'actions',
                value: 'actions',
                noNotAvailableText: true,
                noInput: true
            }
        ]
        if(this.isExpenses) {
            headers.unshift({
                text: this.$t('headers.account'),
                name: 'account_id',
                value: 'account_id',
                setValueExpr: val => this.configs.accounts.find(c => c.id === val)?.name
            })

        }
        return headers
    },

    inputsValidationMessages() {
        const messages = [];
        const inputs = this.tableInputs;
        if (!inputs.materialId) {
            messages.push(this.$t('table-messages.material-required'))
        }
        if (!inputs.quantity) {
            messages.push(this.$t('table-messages.quantity-required'))
        }
        if (!inputs.price) {
            messages.push(this.$t('table-messages.price-required'))
        }
        return messages
    },

    contextmenuItems() {
        return [
            {
                event: 'edit',
                title: this.$t('InvoiceBuyPage.context-menu.edit'),
            }, 
            {
                event: 'delete',
                title: this.$t('InvoiceBuyPage.context-menu.delete'),
            }
        ]
    }
},
created() {
    this.inputsPaymentsOrExpenses.date = moment().format('YYYY-MM-DD')
},
mounted(){
    window.observer.observe(document.querySelector("#tool-bar"))
    window.observer.observe(document.querySelector("#footer"));
    EventBus.$on('warehouse-changed', (warehouse_id, material_id = null)=>{ 
        if(!warehouse_id && material_id)
        {
            // تحديث قائمة مواد المستودع
            this.selected_warehouse_id = this.materials.find(c=>c.id == material_id)?.warehouse_id
            // تعيين المستودع التابع للمادة
            EventBus.$emit('initialize-last-row', { warehouse_id: this.selected_warehouse_id })
        }    
        else
            this.selected_warehouse_id =  warehouse_id
    });

    this.default_warehouse_id = this.configs.warehouse.id
    this.selected_warehouse_id = this.default_warehouse_id
    EventBus.$emit('initialize-last-row', { warehouse_id: this.selected_warehouse_id });

    const promises = [
        this.$store.dispatch("warehouses/fetchAll"),
        this.$store.dispatch("materials/fetchAll"),
        this.$store.dispatch("invoices/fetchAll", invoiceType.buy)
    ];
    
    if(this.$route.params.id) {
        promises.push(
            this.$store.dispatch("invoices/fetch", {
                invoiceId: Number(this.$route.params.id),
                invoiceType: invoiceType.buy
            }).then((data) => {
                this.invoice_no = data.no;
                this.totalPayments = data.transactions.length ? data.transactions.filter(c => c.type === InvoicesTransactionType.payment).map(c => c.amount?.amount).reduce((a, b) => a + b) : null
                this.totalExpenses = data.transactions.length ? data.transactions.filter(c => c.type === InvoicesTransactionType.expensesBuy).map(c => c.amount?.amount).reduce((a, b) => a + b) : null
                this.changeWarehouse();
                this.$refs.table.onEsc();
            })
        )
    } else {
      //  this.$refs.table.openLoading();
        Promise.all(promises).then(() => {
            if(this.simplified_buy_invoices.length > 0) {
                this.$router.push({
                    name: 'invoices-buy',
                    params: { id: this.simplified_buy_invoices[this.simplified_buy_invoices.length - 1].id }
                })
            }
        });
    }
},
methods:{
    moment,
    create(){
        this.loading = true
		store.dispatch("invoices/create",{ invoiceType: invoiceType.buy, invoice: this.invoice_template })
		.then(() => { 
            this.invoice_no = this.buy_invoice.no ;
            this.updateRouteId();
		})
		.finally(() => this.reSet());
    },

    updateRouteId(){
		var invoice = this.simplified_buy_invoices?.find(c => c.no === this.invoice_no)
		if (invoice && invoice.id !== Number(this.$route.params.id)){
			this.$router.push({name: 'invoices-buy', params:{ id: invoice.id }})
		}
    },

    fetchInvoice(){
		var invoice = this.simplified_buy_invoices?.find(c=>c.no == this.invoice_no)
		if(invoice){
			if(invoice.id != Number(this.$route.params.id))
			this.$router.push({name: 'invoices-buy', params:{id: invoice.id}})
				.then(()=>{
					// this.$refs.table.openLoading();
					this.loading = true;
					store.dispatch("invoices/fetch", { invoiceId: Number(this.$route.params.id), invoiceType: invoiceType.buy })
						.then(()=> { 
							this.changeWarehouse();
                            this.loading = false;
							/* this.$refs.table.onEsc(),
							this.$refs.table.closeLoading(); */
                        })
                })
		}
    },
    unPosting(){ 
        this.updateInvoiceLoading = true;
        store.dispatch("invoices/updateToNotPosted", { invoiceId:this.buy_invoice.id, invoiceType: invoiceType.buy })
            .then(()=>{
                Promise.all([
                    store.dispatch("materialsJournals/fetchAll", { fromDate: this.configs.financial_file.start_date, toDate: new Date().toISOString().substr(0, 10) }),
                    store.dispatch("configs/fetchAll"), 
                    store.dispatch("invoices/fetch", { invoiceId:this.buy_invoice.id, invoiceType: invoiceType.buy }),
                    store.dispatch("bonds/fetchAll", { bondType: bondType.accounting })
                ]).finally(() => {
                    this.updateInvoiceLoading = false
                })
            }).catch(() => {
                this.updateInvoiceLoading = false;
            })
    },
    posting(){ 
        this.updateInvoiceLoading = true;
        store.dispatch("invoices/updateToPosted", { invoiceId:this.buy_invoice.id, invoiceType: invoiceType.buy })
            .then(()=>{
                Promise.all([
                    store.dispatch("materialsJournals/fetchAll", { fromDate: this.configs.financial_file.start_date, toDate: new Date().toISOString().substr(0, 10) }),
                    store.dispatch("configs/fetchAll"), 
                    store.dispatch("invoices/fetch", { invoiceId:this.buy_invoice.id, invoiceType: invoiceType.buy }),
                    store.dispatch("bonds/fetchAll", { bondType: bondType.accounting })
                ]).finally(() => {
                    this.updateInvoiceLoading = false;
                })
            }).catch(() => {
                this.updateInvoiceLoading = false;
            })
    },
    handlBlurBoundTable(invoiceEntry) {
        this.messagesSelectRowValidation = invoiceEntryDetectedValidation(invoiceEntry, this.linkedInvoiceRules, { price: this.isWarehousePermission });
    },
    fetchInvoiceEntry(invoiceEntry){
        store.dispatch("invoices/fetchEntry",{ id: invoiceEntry.id, invoiceType: invoiceType.buy })
    },
    async entryHandler() {
        await this.$nextTick();
        if (this.$refs.table.validate()) {
            this.entryLoading = true;
            const actionName = !this.entryId ? 'createEntry' : 'updateEntry' ;
            const entryData = {
                id: this.entryId,
                invoice_id: this.buy_invoice.id,
                material_id: this.tableInputs.materialId,
                quantity: {
                    quantity: this.tableInputs.quantity,
                    unit_size_id: null
                },
                count: this.tableInputs.count,
                notes: this.tableInputs.notes,
                type: invoiceType.buy,
                material_delivery_status: this.tableInputs.material_delivery_status,
                price:
                    this.tableInputs.price
                        ? {
                            amount:  this.tableInputs.price,
                            currency_id: this.currentFinancialFile.default_currency_id
                        }
                        : {
                            amount:  0,
                            currency_id: this.currentFinancialFile.default_currency_id
                        },
                calculated_total_amount:
                    this.tableInputs.calculatedTotalAmount
                        ? {
                            amount:  this.tableInputs.calculatedTotalAmount,
                            currency_id: this.currentFinancialFile.default_currency_id
                        }
                        : {
                            amount:  0,
                            currency_id: this.currentFinancialFile.default_currency_id
                        }
            };
            if (!entryData.id) { delete entryData.id ;}
            store.dispatch(`invoices/${actionName}`, {
                invoiceId: this.buy_invoice.id,
                invoiceType: invoiceType.buy,
                invoiceEntry: entryData
            })
            .then(() => {
                if (this.entryId) {
                    const entryIndex = this.entries.findIndex(c => c.id === this.entryId);
                    if (entryIndex !== -1) {
                        this.entries[entryIndex].warehouse_id = this.tableInputs.warehouseId;
                        this.entries[entryIndex].material_id = this.tableInputs.materialId;
                        this.entries[entryIndex].quantity = this.tableInputs.quantity;
                        this.entries[entryIndex].count = this.tableInputs.count;
                        this.entries[entryIndex].notes = this.tableInputs.notes;
                        this.entries[entryIndex].material_delivery_status = this.tableInputs.material_delivery_status
                    }
                }

                store.dispatch('invoices/fetch', {
                    invoiceId: this.buy_invoice.id,
                    invoiceType: invoiceType.buy
                }).then(() => {
                    this.$refs.table.scrollToRow();
                });
                if (this.entryId) {
                    this.entryId = null;
                    const nextRowIndex = this.$refs.table.editNextRow();
                    if (nextRowIndex !== -1) {
                        this.entryId = this.entries[nextRowIndex].id;
                        this.$refs.table.scrollToRow();
                    } else {
                        this.$refs.table.resetInputs({
                            warehouseId: this.selected_warehouse_id
                        });
                    }
                } else {
                    this.$refs.table.scrollToRow();
                    this.$refs.table.resetInputs({
                        warehouseId: this.selected_warehouse_id
                    });
                }

                this.$nextTick(() => {
                    this.$nextTick(() => {
                        this.$nextTick(() => {
                            if (this.entryId) {
                                setTimeout(() => {
                                    // this.$refs.table.resetInputsFocus();
                                    this.$refs.table.focus('materialId');
                                }, 100);
                            }
                            else {
                                // this.$refs.table.resetInputsFocus();
                                this.$refs.table.focus('materialId');
                            }
                        })
                    })
                })
            })
            .finally(() => {
                this.entryLoading = false;
                // this.$refs.table.resetInputsFocus();
                this.$refs.table.focus('materialId');
            })
        }
    },
    updateEntry(invoiceEntry){        
        let entry = refactorRecord(this.entries[invoiceEntry.rowIndex], this.configs.financial_file.default_currency_id)
        entry.invoice_id = this.buy_invoice.id
        delete entry.discount_percentage
        delete entry.discount_amount
        console.log({ 
            ...entry,
            price: entry.price ? entry.price : {
                amount:  0,
                currency_id: this.currentFinancialFile.default_currency_id
            } ,
            calculated_total_amount: entry.calculated_total_amount ? entry.calculated_total_amount : {
                amount:  0,
                currency_id: this.currentFinancialFile.default_currency_id
            }
        });
        
        if(this.$refs.table.$refs.table.$refs.table.valids[`valid_${invoiceEntry.rowIndex}`])
            store.dispatch("invoices/updateEntry", {
                invoiceId: entry.invoice_id,
                invoiceType: invoiceType.buy,
                invoiceEntry: { 
                    ...entry,
                    price: entry.price ? entry.price : {
                        amount:  0,
                        currency_id: this.currentFinancialFile.default_currency_id
                    } ,
                    calculated_total_amount: entry.calculated_total_amount ? entry.calculated_total_amount : {
                        amount:  0,
                        currency_id: this.currentFinancialFile.default_currency_id
                    }
                }
            })
            .then(() => { 
                store.dispatch("invoices/fetch", { invoiceId: entry.invoice_id, invoiceType: invoiceType.buy })
                invoiceEntry.deleteRowIndex()
            })
            .finally(()=>{
                invoiceEntry.connectionEnded(1, "BeeAutocomplete")
            })
        else
            EventBus.$emit('error', 'تأكد من صحة البيانات المدخلة')
    },
    addInvoiceEntry(invoiceEntry){
        let entry = refactorRecord(invoiceEntry.value, this.configs.financial_file.default_currency_id)
        entry.invoice_id = this.buy_invoice.id
        entry.type = invoiceType.buy
        if (!entry.id) { delete entry.id }
        delete entry.discount_percentage
        delete entry.discount_amount
        console.log({ 
            ...entry,
            price: entry.price ? entry.price : {
                amount:  0,
                currency_id: this.currentFinancialFile.default_currency_id
            } ,
            calculated_total_amount: entry.calculated_total_amount ? entry.calculated_total_amount : {
                amount:  0,
                currency_id: this.currentFinancialFile.default_currency_id
            }
        });
        
        store.dispatch("invoices/createEntry", {
			invoiceId: this.buy_invoice.id,
			invoiceType: invoiceType.buy,
            invoiceEntry: { 
                ...entry,
                price: entry.price ? entry.price : {
                    amount:  0,
                    currency_id: this.currentFinancialFile.default_currency_id
                } ,
                calculated_total_amount: entry.calculated_total_amount ? entry.calculated_total_amount : {
                    amount:  0,
                    currency_id: this.currentFinancialFile.default_currency_id
                }
            }	
        })
        .then(() => {
            store.dispatch("invoices/fetch", { invoiceId: this.buy_invoice.id, invoiceType: invoiceType.buy })
            invoiceEntry.deleteRowData({ warehouse_id: this.selected_warehouse_id })
        }).finally(()=>{
            invoiceEntry.connectionEnded(1, "BeeAutocomplete")
        })
    },
    deleteInvoiceEntry(invoiceEntry){
		this.entry = {...invoiceEntry}
		this.dialogDeleteEntry = true
    },
	confirmDeleteInvoiceEntry(){
		store.dispatch("invoices/deleteEntry", { invoiceId: this.buy_invoice.id, invoiceType: invoiceType.buy, invoiceEntryId: this.entry.id }).then(()=>{
			store.dispatch("invoices/fetch", { invoiceId: this.buy_invoice.id, invoiceType: invoiceType.buy })
		}).finally(()=>{
			this.entry = null
		})
	},
    update(){
        let invoice = {
            external_account_id : this.buy_invoice.external_account_id,
            date: this.buy_invoice.date,
            doc_no: this.buy_invoice.doc_no,
        }
        let required = false
        Object.keys(invoice).forEach((key) => { if(!invoice[key]) required = true; return })

        invoice.total_net = { amount: this.total_net, currency_id: this.configs.financial_file.default_currency_id }
        invoice.notes =  this.buy_invoice.notes
        invoice.discount_total = this.buy_invoice.discount_total

        if(!required && this.buy_invoice.discount_total.amount <= this.total) 
            store.dispatch("invoices/update",{ invoiceId: this.buy_invoice.id, invoiceType: invoiceType.buy, invoice })
            .then(() => { 
                this.$store.dispatch("invoices/fetch", {
                    invoiceId: Number(this.$route.params.id),
                    invoiceType: invoiceType.buy 
                }).then((data) => {
                    this.invoice_no = data.no;
                    this.changeWarehouse();
                    this.$refs.table.onEsc();
                })
                EventBus.$emit("notification", "تم تحديث بيانات الفاتورة");
            })
        else
            EventBus.$emit('error', 'تأكد من صحة البيانات المدخلة')
    },
    changeWarehouse(){
        EventBus.$emit('initialize-last-row', { warehouse_id: this.default_warehouse_id })
        this.selected_warehouse_id = this.default_warehouse_id
        this.tableInputs.warehouseId = this.default_warehouse_id
    },
    reSet(){
        this.loading = false
        this.invoice_template.external_account_id = null
        this.invoice_template.doc_no = null
        this.invoice_template.notes = null
        this.invoice_template.date = new Date().toISOString().substr(0, 10).toString()
        this.addInvoiceDialog = false
        this.$refs.form.resetValidation()
    },
    deleteInvoice(){
        store.dispatch("invoices/delete", { invoiceId:this.buy_invoice.id, invoiceType: invoiceType.buy })
            .then(()=>{ 
                Promise.all([store.dispatch("configs/fetchAll"), store.dispatch("invoices/fetchAll", invoiceType.buy),
                    store.dispatch("bonds/fetchAll", { bondType: bondType.accounting })
                ]).then(()=>{
                    if(this.simplified_buy_invoices.length > 0){
                        this.invoice_no = this.simplified_buy_invoices[this.simplified_buy_invoices.length -1].no
                        this.updateRouteId();
                    }
                    else
                        this.$router.push({ name: 'invoices-buy' })
                            .then(()=> this.$router.go(0) /** Force refresh to empty the store*/ )
                    })
            })
    },

    addRowForEntriesDialog(type) {
        if(this.$refs.tablePayment.validate()) {
            this.submitLoading = true
            const entryData =  {
                invoice_id: this.buy_invoice.id,
                date: this.inputsPaymentsOrExpenses.date,
                amount: {
                    amount: this.inputsPaymentsOrExpenses.amount,
                    currency_id: this.currentFinancialFile.default_currency_id
                },
                type: type,
                notes: this.inputsPaymentsOrExpenses.notes,
                account_id: this.inputsPaymentsOrExpenses.account_id ? this.inputsPaymentsOrExpenses.account_id : null
            }
            this.$store.dispatch('transactions/create', {   
                entryData,
                invoiceType: invoiceType.buy, 
                invoiceId: this.buy_invoice.id 
            }).then(() => {
                this.getAllPaymentsOrExpenses(type)
            })
            this.$refs.tablePayment.resetInputs({
                date: moment().format('YYYY-MM-DD')
            })
        }
    },

    editRowForEntriesDialog(type) {
        this.submitLoading = true
        const entryData =  {
            invoice_id: this.buy_invoice.id,
            date: this.inputsPaymentsOrExpenses.date,
            amount: {
                amount: this.inputsPaymentsOrExpenses.amount,
                currency_id: this.currentFinancialFile.default_currency_id
            },
            type: type,
            notes: this.inputsPaymentsOrExpenses.notes,
            account_id: this.inputsPaymentsOrExpenses.account_id ? this.inputsPaymentsOrExpenses.account_id : null
        }
        this.$store.dispatch('transactions/update', {   
            entryData,
            transactionId: this.transactionId, 
            invoiceId: this.buy_invoice.id,
            invoiceType: invoiceType.buy
        }).then(() => {
            this.getAllPaymentsOrExpenses(type)
        }).finally(() => {
            this.$refs.tablePayment.resetEdit();
            this.isEdit = false;
        })
        this.$nextTick(() => {
            this.$refs.tablePayment.resetInputs({
                date: moment().format('YYYY-MM-DD')
            })
            this.$refs.tablePayment.resetInputsFocus()
        })
    },

    deleteRowForPayments() {
        this.submitLoading = true
        this.$store.dispatch('transactions/delete', { transactionId: this.transactionId }).then(() => {
            this.getAllPaymentsOrExpenses(this.isExpenses ? InvoicesTransactionType.expensesBuy : InvoicesTransactionType.payment)
        }).finally(() => {
            this.submitLoading = false
            this.transactionId = null
        })
    },

    getAllPaymentsOrExpenses(type) {
        this.submitLoading = true
        this.$store.dispatch('transactions/fetchTransaction', { 
            invoiceType: invoiceType.buy, 
            invoiceId: this.buy_invoice.id,
            transactionType: type
        }).then((data) => {
            this.entriesDialog = data.data
            if(!this.isExpenses) {
                this.totalPayments = this.entriesDialog.length ? this.entriesDialog.map(c => c.amount.amount).reduce((a, b) => a + b) : null
            } else {
                this.totalExpenses = this.entriesDialog.length ? this.entriesDialog.map(c => c.amount.amount).reduce((a, b) => a + b) : null
            }
            this.submitLoading = false
        }).finally(() => {
            this.submitLoading = false
        })
    },

    confirmTotalPaymentsForTotalNet() {
        const type = !this.isExpenses ? InvoicesTransactionType.payment : InvoicesTransactionType.expensesBuy
        if (this.isExpenses) {
            if(!this.isEdit) {
                this.addRowForEntriesDialog(type)
            } else {
                this.editRowForEntriesDialog(type)
            }
        } else {
            if (
                (((this.totalPayments + this.inputsPaymentsOrExpenses.amount) > this.total_net) && !this.isEdit)
            ) {
                this.alertMessageDialog = true
            } else if(
                (    
                    ((this.totalPayments - this.entriesDialog[this.editRowIndex].amount.amount + this.inputsPaymentsOrExpenses.amount) > this.total_net) && 
                    this.isEdit
                )
            ) {
                this.alertMessageDialog = true
            }
            else {
                if(!this.isEdit) {
                    this.addRowForEntriesDialog(type)
                } else {
                    this.editRowForEntriesDialog(type)
                }
            }
        }
        
    },
},
/*
async beforeRouteEnter(to, from, next) {
	let promises = [store.dispatch("warehouses/fetchAll"), store.dispatch("materials/fetchAll"), store.dispatch("invoices/fetchAll", 'Buy')]
	let object = JSON.parse(JSON.stringify(store.state.configs.configs))
	if(Object.keys(object).length == 0)
		promises.push(store.dispatch("configs/fetchAll"))

	if(promises.length)
		await Promise.all(promises)

    if(to.params.id)
	{
		if(to.name != 'invoice-buy')
			next({name: 'invoice-buy', params:{id: to.params.id}})
	}	
	else
	{
		let buy_invoices = JSON.parse(JSON.stringify(store.state.invoices.simplified_buy_invoices))
		if(buy_invoices.length > 0)
			next({ name: 'invoice-buy', params:{id: buy_invoices[buy_invoices.length -1].id }})
	}

	next(
		vm => {
			if(to.name == "invoice-buy")
			{
				if(to.params.id)
				{
					vm.$refs.table.openLoading();
					store.dispatch("invoices/fetch", { invoiceId: Number(to.params.id), invoiceType:"Buy" })
						.then(response=> { 
							vm.invoice_no = response.no
							vm.changeWarehouse()
							vm.$refs.table.onEsc(),
							vm.$refs.table.closeLoading();})
				}
			} 
		})
}*/
}
</script>
<style>
.my-border {
	border: 1px solid !important;
	border-color: #f1f1f1 !important;	
}
.v-bottom-navigation {
	overflow-y: unset;
	box-shadow: 0px 10px 5px #eee, 0px -2px 5px gainsboro !important;
	background: linear-gradient(180deg, rgba(241,241,241,1) 0%, rgba(230,240,245,1) 100%);
}
.fill-height
{
height: 100% !important;
}
</style>
